import {useAtom, useAtomValue} from 'jotai';
import {useMemo} from 'react';
import styled from 'styled-components';
import {ChevronRightIconComponent} from '@/components/Icons';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {AnalysisItem} from '@/map/@types';
import {useKodataNearbyStoreCountByPoint} from '@/map/api';
import {mapAgentTypeAtom, selectedNearbyStoreCategoryAtom} from '@/map/atom';
import {
  ColumnFlexBox,
  Flexbox,
  FlexCenter,
  FlexFullGrow,
  PaddingBox,
} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {CategoryTabs} from '../CategoryTabs';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {
  ReportContainer,
  SectionLabel,
  Table,
  TableCell,
  TableHeaderRow,
  TableRow,
} from '../StyledComponents';
import {CategoryStores} from './CategoryStores';
import {useGA4WithParams} from '@/hooks/tracking';
import {AreaOverlayType} from '@/map/enums';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

export const NearbyStores = ({item, ...props}: IProps) => {
  if (!item.circle?.radius || item.circle.radius > 1_000) {
    return (
      <PaddingBox top={28}>
        <NoData text="주변 매장 정보를 조회할 수 없어요.<br />분석 반경을 1km이하로 변경해주세요." />
      </PaddingBox>
    );
  }

  return <Report item={item} {...props} />;
};

const Report = ({map, item}: IProps) => {
  const {data, isPending, error} = useKodataNearbyStoreCountByPoint(item);
  const sorted = useMemo(
    () =>
      data &&
      [...data].filter((i) => i.count > 0).sort((a, b) => b.count - a.count),
    [data]
  );

  const [activeCategory, setCategory] = useAtom(
    selectedNearbyStoreCategoryAtom
  );

  const categories = sorted?.map((i) => i.category);
  const totalStoreCount = sorted?.reduce((acc, cur) => acc + cur.count, 0);

  const mapAgent = useAtomValue(mapAgentTypeAtom);

  const tracking = useGA4WithParams();

  const onClick = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    const {key} = e.currentTarget.dataset;

    if (key) {
      setCategory(key);
      tracking('aroundInfo_circle_mobile', {
        radius: item?.[AreaOverlayType.CIRCLE]?.radius,
        businessCategory: key,
        agent: mapAgent,
      });
    } else {
      setCategory(null);
    }
  };

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <ColumnFlexBox gap={16}>
          <LoadingBar color={palette.textSecondary} />
          <FlexCenter>
            <Typography variant="paragraph14" align="center">
              주변 매장 데이터를 불러오고 있어요.
              <br />
              반경 범위를 줄이면 더 빠르게 결과를 확인할 수 있어요!
            </Typography>
          </FlexCenter>
        </ColumnFlexBox>
      </ReportContainer>
    );
  }

  if (!sorted?.length) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 주변 매장 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      {!activeCategory ? (
        <ReportContainer>
          <div>
            <SectionLabel>업종별 주변 매장 현황</SectionLabel>
          </div>
          <PaddingBox top={8}>
            <Typography variant="medium12" color={palette.gray600}>
              선택한 반경 이내에 위치한 업종별 총 매장 수 입니다.
              <br />
              매장 수가 많을 수록 해당 업종의 경쟁이 치열할 수 있습니다.
            </Typography>
          </PaddingBox>
          <PaddingBox top={20}>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableCell>업종</TableCell>
                  <TableCell>총 매장 수</TableCell>
                </TableHeaderRow>
              </thead>
              <tbody>
                {sorted.map((i) => (
                  <TableRow key={i.category}>
                    <TableCell>{i.category}</TableCell>
                    <TableCell>
                      <Flexbox>
                        <FlexFullGrow />
                        <Button data-key={i.category} onClick={onClick}>
                          {withComma(i.count)}
                          <PaddingBox spacing={4}>
                            <ChevronRightIconComponent
                              color={palette.teal100}
                            />
                          </PaddingBox>
                        </Button>
                      </Flexbox>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </PaddingBox>
        </ReportContainer>
      ) : (
        <CategoryTabs
          prevButtonText="업종 현황"
          categories={categories}
          activeTab={activeCategory}
          onClick={onClick}
        />
      )}
      <CategoryStores
        activeCategory={activeCategory}
        totalCount={totalStoreCount}
        item={item}
        map={map}
      />
    </>
  );
};

const Button = styled.button`
  color: ${palette.teal100};
  display: flex;
  align-items: center;
  jusify-content: flex-end;
  padding-left: 6px;
  svg {
    display: block;
  }
`;
